import React, { Component } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, withRouter } from "react-router-dom";
import { PUBLIC_URL } from "../config";
import { connect } from "react-redux";
import { addCart } from "../store/actions/cart";
import SimpleImageLoader from "./shared/SimpleImageLoader";
import { trackSelectItem, trackAddToCart } from "../utils/analytics";

// Vibrant CSS animations
const vibrantStyles = `
  @keyframes slideInUp {
    from { 
      opacity: 0; 
      transform: translateY(30px); 
    }
    to { 
      opacity: 1; 
      transform: translateY(0); 
    }
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }

  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }

  @keyframes glow {
    0%, 100% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.4); }
    50% { box-shadow: 0 0 30px rgba(99, 102, 241, 0.6); }
  }

  @keyframes newBadge {
    0%, 100% { transform: rotate(-15deg) scale(1); }
    50% { transform: rotate(-15deg) scale(1.1); }
  }

  @keyframes lowStockBlink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0.7; }
  }

  @keyframes dealCountdown {
    0%, 100% { color: #ef4444; }
    50% { color: #dc2626; }
  }

  @keyframes bestsellerCrown {
    0%, 100% { transform: rotate(-15deg) scale(1); }
    50% { transform: rotate(-15deg) scale(1.1); }
  }

  .minimal-badge {
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: white;
    z-index: 10;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.15);
  }
  
  .badge-stack {
    position: absolute;
    top: 8px;
    left: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    z-index: 15;
    max-width: calc(100% - 80px);
    flex-wrap: wrap;
  }
  
  .badge-top-right {
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 15;
  }

  .vibrant-card {
    animation: slideInUp 0.5s ease-out;
  }

  .vibrant-card:hover {
    transform: translateY(-8px) !important;
  }

  .vibrant-card:hover .product-image {
    transform: scale(1.02);
  }

  .product-image {
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .vibrant-card:hover .product-image {
    transform: scale(1.05);
  }

  .free-shipping-badge {
    animation: pulse 2s infinite;
  }

  .discount-percentage {
    animation: glow 2s infinite;
  }
`;

class VibrantProductCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isHovered: false,
      isAdding: false,
    };
    this.handleAddToCart = this.handleAddToCart.bind(this);
  }

  componentDidMount() {
    // Add vibrant styles
    VibrantProductCard.injectStyles();
  }

  static injectStyles() {
    if (!document.getElementById("vibrant-product-styles")) {
      const styleSheet = document.createElement("style");
      styleSheet.id = "vibrant-product-styles";
      styleSheet.innerText = vibrantStyles;
      document.head.appendChild(styleSheet);
    }
  }


  getImageSrc() {
    if (!this.props.image) return "/images/placeholder.jpg";

    // Use config's PUBLIC_URL function - single source of truth
    try {
      return PUBLIC_URL(this.props.image);
    } catch (error) {
      // Production'da hata loglama - sadece development'ta göster
      if (process.env.NODE_ENV === 'development') {
        console.error("Error constructing image URL:", error, this.props.image);
      }
      return "/images/placeholder.jpg";
    }
  }

  calculateDiscountPercentage() {
    if (!this.props.sale_price) return 0;
    const discount =
      ((this.props.price - this.props.sale_price) / this.props.price) * 100;
    return Math.round(discount);
  }

  handleAddToCart(e) {
    e.preventDefault();
    e.stopPropagation();

    this.setState({ isAdding: true });

    // Analytics tracking - ürün sepete ekleniyor
    trackAddToCart(this.props, 1);

    this.props.addCart(this.props, 1);

    setTimeout(() => {
      this.setState({ isAdding: false });
    }, 2000);
  }

  handleProductClick = (e) => {
    // Prevent if clicking on button
    if (e.target.closest('button')) {
      return;
    }

    // Analytics tracking - ürün seçildi
    if (this.props.listName && this.props.listIndex !== undefined) {
      trackSelectItem(this.props, this.props.listName, this.props.listIndex);
    }

    // Navigate to product page
    this.props.history.push(`/urun/${this.props.slug}`, { product: this.props });
  };

  formatPrice(price) {
    if (!price || isNaN(price)) return "0,00";
    return Number(price).toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  formatPriceCompact(price) {
    if (!price || isNaN(price)) return "0";
    return Number(price).toLocaleString("tr-TR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  checkFreeShipping() {
    // Get free shipping threshold from settings (default 350 TL)
    const freeShippingThreshold =
      (this.props.settings && this.props.settings.setting && this.props.settings.setting.free_shipping) || 350;
    // Use sale_price if available (discounted price), otherwise use regular price
    const currentPrice = this.props.sale_price || this.props.price;
    return currentPrice >= freeShippingThreshold;
  }

  checkIsNew() {
    // Check if product is explicitly marked as new
    if (this.props.isNewProduct) {
      // If newUntilDate is set, check if it's still valid
      if (this.props.newUntilDate) {
        const now = new Date();
        const newUntilDate = new Date(this.props.newUntilDate);
        return now <= newUntilDate;
      }
      // If marked as new but no end date, show as new
      return true;
    }

    // Fallback: Check if product is created within last 7 days
    if (this.props.created_at) {
      const now = new Date();
      const createdDate = new Date(this.props.created_at);
      const daysDifference = (now - createdDate) / (1000 * 60 * 60 * 24);
      return daysDifference <= 7;
    }

    return false;
  }

  checkLowStock() {
    const threshold = this.props.lowStockThreshold || 5;
    return (
      this.props.stock != null &&
      this.props.stock > 0 &&
      this.props.stock <= threshold
    );
  }

  formatCountdown(timeRemaining) {
    if (!timeRemaining) return null;

    const { days, hours, minutes } = timeRemaining;

    if (days > 0) {
      return `${days}g ${hours}s ${minutes}d`;
    } else if (hours > 0) {
      return `${hours}s ${minutes}d`;
    } else {
      return `${minutes}d`;
    }
  }

  render() {
    const { isHovered, isAdding } = this.state;
    const discountPercentage = this.calculateDiscountPercentage();
    const hasFreeShipping = this.checkFreeShipping();
    const isNew = this.checkIsNew();
    const isLowStock = this.checkLowStock();
    const countdownText = this.props.timeRemaining
      ? this.formatCountdown(this.props.timeRemaining)
      : null;

    // Ensure we have required props
    if (
      !this.props.name ||
      this.props.price === undefined ||
      this.props.price === null
    ) {
      return null;
    }

    return (
      <div
        className="vibrant-card"
        style={{
          background: "#fff",
          borderRadius: "16px",
          overflow: "hidden",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: isHovered
            ? "0 20px 40px rgba(0, 0, 0, 0.15)"
            : "0 4px 12px rgba(0, 0, 0, 0.06)",
          cursor: "pointer",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          border: "1px solid #f3f4f6",
        }}
        onMouseEnter={() => this.setState({ isHovered: true })}
        onMouseLeave={() => this.setState({ isHovered: false })}
        onClick={this.handleProductClick}
      >
        {/* Badge Container - Top Right Stack */}
        <div
          style={{
            position: "absolute",
            top: "8px",
            marginTop: hasFreeShipping ? "34px" : "0",
            right: "8px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            zIndex: 15,
            alignItems: "flex-end",
          }}
        >
          {/* Discount Percentage Badge */}
          {discountPercentage > 0 && this.props.price > 0 && (
            <div
              style={{
                background: "rgba(136, 192, 0, 0.9)",
                color: "#fff",
                padding: "4px 8px",
                borderRadius: "12px",
                fontSize: "10px",
                fontWeight: "600",
                textAlign: "center",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
                backdropFilter: "blur(8px)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
              }}
            >
              %{discountPercentage}
            </div>
          )}

          {/* Yeni Badge */}
          {isNew ? (
            <div
              className="minimal-badge"
              style={{
                background: "rgba(251, 191, 36, 0.9)",
              }}
            >
              Yeni
            </div>
          ) : null}
        </div>
        {/* Top Banner - Free Shipping */}
        {hasFreeShipping && (
          <div
            className="free-shipping-badge"
            style={{
              position: "absolute",
              top: "0",
              left: "0",
              right: "0",
              background: "#88c000",
              color: "#f0fff4",
              padding: "8px",
              fontSize: "12px",
              fontWeight: "700",
              textAlign: "center",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              zIndex: 15,
              borderRadius: "16px 16px 0 0",
              textShadow: "0 1px 2px rgba(0,0,0,0.2)",
            }}
          >
            KARGO BEDAVA
          </div>
        )}
        {/* Badge Container - Top Left Stack */}
        <div
          className="badge-stack"
          style={{ marginTop: hasFreeShipping ? "34px" : "0" }}
        >
          {/* Best Seller Badge - Minimal */}
          {this.props.isBestSellerSection && this.props.isBestSeller ? (
            <div
              className="minimal-badge"
              style={{
                background: "rgba(255, 215, 0, 0.9)",
                color: "#1f2937",
                display: "flex",
                alignItems: "center",
                gap: "2px",
                position: "relative",
              }}
            >
              <FontAwesomeIcon
                icon={["fas", "star"]}
                style={{ fontSize: "7px" }}
              />
              Çok Satan
            </div>
          ) : null}

          {/* Low Stock Badge - Minimal */}
          {isLowStock ? (
            <div
              className="minimal-badge"
              style={{
                background: "rgba(239, 68, 68, 0.9)",
                position: "relative",
              }}
            >
              {this.props.stock} Adet Kaldı
            </div>
          ) : null}
        </div>
        {/* Deal Countdown Timer */}
        {countdownText && this.props.isDeal ? (
          <div
            style={{
              position: "absolute",
              bottom: "12px",
              left: "12px",
              right: "12px",
              background: "rgba(0, 0, 0, 0.8)",
              color: "#fff",
              padding: "6px 8px",
              borderRadius: "8px",
              fontSize: "11px",
              fontWeight: "600",
              zIndex: 9,
              textAlign: "center",
              animation: "dealCountdown 1s infinite",
            }}
          >
            <span role="img" aria-label="fire">
              🔥
            </span>{" "}
            {countdownText} KALDI
          </div>
        ) : null}
        {/* Image Container */}
        <Link
          to={`/urun/${this.props.slug}`}
          style={{ textDecoration: "none", display: "block" }}
          onClick={() => this.handleProductClick("Ürün Resmi")}
        >
          <div
            className="product-image-container"
            style={{
              position: "relative",
              height: "280px", // taller, portrait-friendly area
              overflow: "hidden",
              background: "linear-gradient(180deg, #f9fafb 0%, #f3f4f6 100%)",
              marginTop: hasFreeShipping ? "28px" : "0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SimpleImageLoader
              className="product-image"
              src={this.getImageSrc()}
              alt={this.props.name}
              fallbackSrc="/images/placeholder.jpg"
              placeholderColor="#f8f9fa"
              imgStyle={{
                objectFit: "contain",
                transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            />
          </div>
        </Link>
        {/* Product Info */}
        <div
          style={{
            padding: "16px",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Brand */}
          <p
            style={{
              fontSize: "10px",
              color: "#9ca3af",
              marginBottom: "4px",
              fontWeight: "600",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {(this.props.brand && this.props.brand.name) || "BOTANIKSAN"}
          </p>

          {/* Product Name */}
          <Link
            to={`/urun/${this.props.slug}`}
            style={{ textDecoration: "none" }}
            onClick={() => this.handleProductClick("Ürün Adı")}
          >
            <h3
              style={{
                fontSize: "14px",
                fontWeight: "500",
                color: "#1f2937",
                marginBottom: "8px",
                lineHeight: "1.4",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                minHeight: "40px",
                transition: "color 0.2s ease",
              }}
            >
              {this.props.name}
            </h3>
          </Link>

          {/* Price Section */}
          <div
            style={{
              marginTop: "auto",
              marginBottom: "12px",
            }}
          >
            {this.props.sale_price && this.props.sale_price > 0 ? (
              <div>
                {/* İndirimli Fiyat Layout - Yan Yana */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  {/* İndirim Yüzdesi Badge - Sol */}
                  {discountPercentage > 0 && (
                    <div
                      style={{
                        background: "rgba(136, 192, 0, 0.9)",
                        color: "#fff",
                        padding: "6px 8px",
                        borderRadius: "4px",
                        fontSize: "13px",
                        fontWeight: "700",
                        minWidth: "52px",
                        height: "40px",
                        textAlign: "center",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      %{discountPercentage}
                    </div>
                  )}

                  {/* Fiyat Bilgileri - Sağ */}
                  <div
                    style={{
                      flex: 1,
                      width: discountPercentage > 0 ? "auto" : "100%",
                    }}
                  >
                    {/* Eski Fiyat (Üstü Çizili) */}
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#9ca3af",
                        textDecoration: "line-through",
                        marginBottom: "2px",
                        lineHeight: "1.2",
                      }}
                    >
                      {this.formatPriceCompact(this.props.price)} ₺
                    </div>

                    {/* Yeni Fiyat */}
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "#1f2937",
                        lineHeight: "1.2",
                      }}
                    >
                      {this.formatPriceCompact(this.props.sale_price)} ₺
                    </div>
                  </div>
                </div>

                {/* Sepette İndirim Mesajı */}
                <div
                  style={{
                    background: "rgba(136, 192, 0, 0.1)",
                    border: "1px solid rgba(136, 192, 0, 0.3)",
                    padding: "6px 8px",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: "600",
                    color: "#5a7c02",
                    textAlign: "center",
                  }}
                >
                  Sepette %{discountPercentage} İndirim
                </div>
              </div>
            ) : (
              <div
                style={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: "#1f2937",
                }}
              >
                {this.props.price === 0
                  ? "Fiyat Belirtilmemiş"
                  : `${this.formatPriceCompact(this.props.price)} ₺`}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              gap: "8px",
            }}
          >
            <button
              onClick={this.handleAddToCart}
              disabled={
                isAdding || this.props.price === 0 || this.props.stock === 0
              }
              style={{
                flex: 1,
                padding: "12px",
                background:
                  this.props.price === 0 || this.props.stock === 0
                    ? "#9ca3af"
                    : isAdding
                    ? "#66cc33"
                    : "#88c000",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "600",
                cursor:
                  isAdding || this.props.price === 0 || this.props.stock === 0
                    ? "default"
                    : "pointer",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                textTransform: "uppercase",
                transform: isAdding ? "scale(0.95)" : "scale(1)",
                opacity:
                  this.props.price === 0 || this.props.stock === 0 ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (
                  !isAdding &&
                  this.props.price !== 0 &&
                  this.props.stock !== 0
                ) {
                  e.target.style.background = "#66cc33";
                  e.target.style.transform = "scale(1.02)";
                }
              }}
              onMouseLeave={(e) => {
                if (
                  !isAdding &&
                  this.props.price !== 0 &&
                  this.props.stock !== 0
                ) {
                  e.target.style.background = "#88c000";
                  e.target.style.transform = "scale(1)";
                }
              }}
            >
              <FontAwesomeIcon
                icon={[
                  "fas",
                  this.props.stock === 0
                    ? "times-circle"
                    : this.props.price === 0
                    ? "info-circle"
                    : isAdding
                    ? "check-circle"
                    : "shopping-cart",
                ]}
              />
              {this.props.stock === 0
                ? "STOKTA YOK"
                : this.props.price === 0
                ? "FİYAT YOK"
                : isAdding
                ? "EKLENDİ"
                : "SEPETE EKLE"}
            </button>

            <Link
              to={`/urun/${this.props.slug}`}
              style={{
                padding: "12px",
                background: "transparent",
                color: "#88c000",
                border: "2px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "600",
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.3s ease",
              }}
              onClick={() => this.handleProductClick("Görüntüle Butonu")}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#88c000";
                e.currentTarget.style.background = "#f0f8e8";
                e.currentTarget.style.color = "#66cc33";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "#88c000";
              }}
            >
              <FontAwesomeIcon icon={["fas", "eye"]} />
            </Link>
          </div>

          {/* Stock & Delivery Info */}
          <div
            style={{
              marginTop: "12px",
              paddingTop: "12px",
              borderTop: "1px solid #f3f4f6",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Stock Status */}
            {this.props.stock !== undefined && (
              <div
                style={{
                  fontSize: "11px",
                  color: this.props.stock > 0 ? "#66cc33" : "#ef4444",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <FontAwesomeIcon
                  icon={[
                    "fas",
                    this.props.stock > 0 ? "check-circle" : "times-circle",
                  ]}
                />
                {this.props.stock > 0 ? "Stokta" : "Tükendi"}
              </div>
            )}

            {/* Fast Delivery */}
            <div
              style={{
                fontSize: "11px",
                color: "#6b7280",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <FontAwesomeIcon icon={["fas", "shipping-fast"]} />
              Hızlı Teslimat
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  settings: state.setting || {},
});

const mapDispatchToProps = {
  addCart,
};

// Initialize styles when module loads
VibrantProductCard.injectStyles();

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(VibrantProductCard));



// WEBPACK FOOTER //
// ./src/components/VibrantProductCard.js